/**
 * La puerta de una pantalla (2026-10-01, con los permisos detallados).
 *
 * Desde que los permisos se pueden dar pantalla por pantalla —«Estadísticas» sí,
 * «Utilidad y pérdida» no—, esconder la opción del menú no basta: quien escribe
 * la dirección a mano, o abre un enlace guardado, llegaría a una pantalla que
 * solo da errores del servidor. Con esto ve un aviso claro.
 *
 * ⚠️ Es solo para que se vea bien. La seguridad la pone el servidor: cada
 * endpoint exige su permiso (`app/core/permissions.py`).
 */

import type { ReactNode } from "react";
import { Link, Navigate } from "react-router-dom";
import { Lock } from "lucide-react";
import { Boton, Vacio } from "@/components/ui";
import { useAuth } from "@/stores/auth";

export function ConPermiso({
  permiso,
  alguno = false,
  enSuLugar,
  children,
}: {
  /** El permiso que exige la pantalla. Con varios, hacen falta todos… */
  permiso: string | string[];
  /** …salvo con `alguno`: basta uno (el Calendario, que junta varias áreas). */
  alguno?: boolean;
  /**
   * Adónde llevar a quien no lo tiene pero sí tiene otra sección hermana: quien
   * solo ve «Estadísticas» y entra a Reportes, va directo a Estadísticas.
   */
  enSuLugar?: { permiso: string; a: string }[];
  children: ReactNode;
}) {
  const { puede, esDueno } = useAuth();
  const exigidos = Array.isArray(permiso) ? permiso : [permiso];
  const pasa = alguno ? exigidos.some((p) => puede(p)) : exigidos.every((p) => puede(p));
  if (esDueno() || pasa) return <>{children}</>;

  const otra = enSuLugar?.find((o) => puede(o.permiso));
  if (otra) return <Navigate to={otra.a} replace />;
  return <SinAcceso />;
}

/** El aviso de una sección no habilitada para quien la abre. */
export function SinAcceso() {
  return (
    <Vacio
      icono={<Lock size={24} />}
      titulo="Sección no habilitada"
      descripcion="Su usuario no tiene acceso a esta sección. Puede solicitarlo al dueño del negocio."
      accion={
        <Link to="/">
          <Boton variante="secundario">Ir al inicio</Boton>
        </Link>
      }
    />
  );
}
